/**
 * Unified analytics helpers — fan events out to both
 *   • Google Analytics 4 (gtag.js)
 *   • Microsoft Clarity (heatmaps + session recordings)
 *
 * Both scripts are loaded in /public/index.html. We set send_page_view: false
 * on GA so the SPA can emit page_view on every react-router location change.
 * Clarity auto-tracks page activity, but we also push the path as a session
 * tag so recordings can be filtered by route.
 */

const GA_ID = "G-RD7ZQBVDFH";

function getGtag() {
  if (typeof window === "undefined") return null;
  return typeof window.gtag === "function" ? window.gtag : null;
}

function getClarity() {
  if (typeof window === "undefined") return null;
  return typeof window.clarity === "function" ? window.clarity : null;
}

/** Fire a GA4 page_view + tag the Clarity session with the current path. */
export function trackPageview(path, title) {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.origin + path,
      send_to: GA_ID,
    });
  }
  const clarity = getClarity();
  if (clarity) {
    // Session-level tag so Clarity recordings/heatmaps can be filtered by route
    try {
      clarity("set", "page", path);
      clarity("event", "page_view");
    } catch (_) {
      /* clarity loads async — calls before ready are queued automatically */
    }
  }
}

/**
 * Fire a custom event into BOTH analytics platforms.
 * Clarity custom events take only a name; we additionally `set` each scalar
 * param as a session tag so they show up in dashboard filters.
 */
export function trackEvent(eventName, params = {}) {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", eventName, { send_to: GA_ID, ...params });
  }
  const clarity = getClarity();
  if (clarity) {
    try {
      clarity("event", eventName);
      // Push scalar params as session tags so Clarity can filter recordings.
      for (const k of Object.keys(params || {})) {
        const v = params[k];
        if (v == null) continue;
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
          clarity("set", `${eventName}.${k}`, String(v));
        }
      }
    } catch (_) {
      /* swallow — clarity queue handles pre-init calls */
    }
  }
}

/** Convenience: standardized CTA click. */
export function trackCta(label, extra = {}) {
  trackEvent("cta_click", {
    cta_label: label,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    ...extra,
  });
}
